import {useCallback, useContext, useEffect} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useRouter} from '@s-ui/react-router'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'
import {useNotificationManager} from '@adv-mt/mt-hook-use-notification-manager'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import ButtonCreateAlert, {buttonSizes, buttonTypes} from './button-create-alert.js'
import NotificationNoFiltersApplied from './notification-no-filters-applied.js'
import SearchSaveDuplicatedAlertErrorNotification from './search-save-duplicated-alert-error-notification.js'
import SearchSaveErrorNotification from './search-save-error-notification.js'
import SearchSaveMaxLimitReachedNotification from './search-save-max-limit-reached-notification.js'
import SearchSaveSuccessNotification from './search-save-success-notification.js'
import SearchSaveTooltip from './search-save-tooltip.js'
import useSearchSaveState from './use-search-save-state.js'

const BASE_CLASS = 'mt-SearchSave'
const TRACKING_EVENTS = {
  ALERT_CREATED_WITHOUT_FILTERS: 'Alert Without Filters Created',
  ALERT_CREATED: 'Alert Created',
  CREATE_ALERT_BUTTON_CLICKED: 'Create Alert Clicked',
  NEED_FILTERS_TOOLTIP_SHOWN: 'Tooltip Filters Triggered',
  TOOLTIP_CREATE_ALERT_SHOWN: 'Tooltip Create Alert Triggered',
  ALERT_CREATED_ZERO_RESULTS: 'Filter 0 Result Icon Actioned'
}
const TRACKING_ORIGINS = {
  ADS_LIST: 'List Alert Created',
  AD_DETAIL: 'Detail Alert Created',
  AFTER_CONTACT_AD_DETAIL: 'After Contact Detail Alert Created',
  RECENT_SEARCHES: 'Recent Searches Alert Created',
  NET_ASSISTANT: 'NET Alert Created'
}

const TRACKING_INFORMATION = {
  CREATE_ALERT: 'Crear alerta'
}

const SearchSave = ({
  adId,
  createdAlert,
  delay,
  hasZeroResults = false,
  isFullWidth = true,
  isSearchModal = false,
  search,
  setLoginModalVisible = () => {},
  showTooltip = true,
  size,
  trackingOrigin,
  authenticationHitInformation2,
  type,
  hideText,
  customText,
  icon,
  disabled,
  onSuccess,
  createAlertAfterLogin,
  portalContainer,
  extendedTrackingData = {},
  notificationClassName
}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const {
    COOKIES: {IS_VIEWED_ZERO_RESULTS_TOOLTIP}
  } = config.get('COMMON')
  const {showNotification: showedZeroResultsTooltip, setIsViewed: setIsViewedZeroResultsTooltip} =
    useNotificationManager({
      key: IS_VIEWED_ZERO_RESULTS_TOOLTIP
    })

  const {track} = useAnalyticsTrack()
  const {push} = useRouter()
  const {triggerLoginModal, isLoginModalOpen} = useContext(LoginModalContext)

  // STATE
  const [state, actions] = useSearchSaveState({})

  const {
    alert,
    isSuccessNotificationVisible,
    isErrorNotificationVisible,
    isFiltersNotAppliedNotificationVisible,
    isDuplicatedAlertErrorNotificationVisible,
    isMaxLimitReachedAlertNotificationVisible,
    isMinimumFiltersAppliedNotificationVisible,
    notAvailableFilters
  } = state

  const {
    setAlert,
    showSuccessNotification,
    hideSuccessNotification,
    showErrorNotification,
    hideErrorNotification,
    hideFiltersNotAppliedNotification,
    showDuplicatedAlertErrorNotification,
    hideDuplicatedAlertErrorNotification,
    showMaxLimitReachedAlertNotification,
    hideMaxLimitReachedAlertNotification,
    showMinimumFiltersAppliedNotification,
    hideMinimumFiltersAppliedNotification,
    hideAlertsTooltip
  } = actions

  const {
    HTTP_STATUS_CODES: {FORBIDDEN: noPermissionErrorCode, CONFLICT: duplicatedAlertErrorCode},
    SEARCH_SAVE: {MAX_ALERTS_LIMIT_REACHED_ERROR_CODE}
  } = config.get('COMMON')

  const {
    ERRORS: {SearchHasNoFiltersUserAlertError}
  } = config.get('COMMON')

  useEffect(() => {
    setLoginModalVisible(isLoginModalOpen)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoginModalOpen])

  const loginWithModal = () => {
    const {CREATE_ALERT_QUERY_PARAM} = config.get('search')
    const {LOGIN_SOURCE_ID_CREATE_ALERT} = config.get('login')
    const {HIT_ORIGINS} = config.get('tracking')

    triggerLoginModal({
      modalProps: {
        source: LOGIN_SOURCE_ID_CREATE_ALERT,
        trackingOrigin: HIT_ORIGINS.SEARCH_SAVED,
        hitInformation2: authenticationHitInformation2,
        onLogin: () => {
          if (createAlertAfterLogin) {
            domain
              .get('create_alert_user_alert_use_case')
              .execute({search, adId})
              .then(alert => {
                setAlert(alert)
                onSuccess?.()
              })
              .catch(handleCreateAlertError)
          } else {
            const _maybeCreateAlert = async () => {
              const urlToRedirect = new URL(location.toString())
              urlToRedirect.searchParams.append(CREATE_ALERT_QUERY_PARAM, 'true')

              push(urlToRedirect.toString())
            }
            _maybeCreateAlert()
          }
        }
      }
    })
  }

  const handleCreateAlertError = useCallback(
    async error => {
      if (error instanceof SearchHasNoFiltersUserAlertError) {
        track({event: TRACKING_EVENTS.ALERT_CREATED_WITHOUT_FILTERS})
        track({event: TRACKING_EVENTS.NEED_FILTERS_TOOLTIP_SHOWN})
        return showMinimumFiltersAppliedNotification()
      }
      switch (error.code) {
        case noPermissionErrorCode:
          loginWithModal()
          break
        case duplicatedAlertErrorCode:
          showDuplicatedAlertErrorNotification({notAvailableFilters: error.notAvailableFilters})
          break
        case MAX_ALERTS_LIMIT_REACHED_ERROR_CODE:
          showMaxLimitReachedAlertNotification()
          break
        default:
          showErrorNotification()
      }
    },
    [search] // eslint-disable-line
  )

  const trackAndNotify = ({alert}) => {
    const {alertId} = alert
    hasZeroResults
      ? track({
          event: TRACKING_EVENTS.ALERT_CREATED_ZERO_RESULTS,
          data: {
            hit_information: TRACKING_INFORMATION.CREATE_ALERT
          }
        })
      : track({
          event: TRACKING_EVENTS.ALERT_CREATED,
          data: {
            ...(trackingOrigin && {hit_origin: trackingOrigin}),
            ...(alertId && {hit_id: alertId}),
            ...extendedTrackingData
          }
        })
    showSuccessNotification()
  }

  useEffect(() => {
    if (createdAlert) {
      setAlert(createdAlert)
    }
  }, [createdAlert]) // eslint-disable-line

  useEffect(() => {
    alert && alert.alertId && trackAndNotify({alert})
  }, [alert])

  const handleCreateAlert = async e => {
    // We want to control the onClick execution of the Popover, this is why we do this.
    e.stopPropagation()

    track({event: TRACKING_EVENTS.CREATE_ALERT_BUTTON_CLICKED})
    hasZeroResults && !showedZeroResultsTooltip && setIsViewedZeroResultsTooltip()

    domain
      .get('create_alert_user_alert_use_case')
      .execute({search, adId})
      .then(alert => {
        setAlert(alert)
        hideAlertsTooltip()
        onSuccess?.()
      })
      .catch(handleCreateAlertError)

    hideAlertsTooltip()
  }

  const hasToRenderNotification =
    isSuccessNotificationVisible ||
    isErrorNotificationVisible ||
    isDuplicatedAlertErrorNotificationVisible ||
    isMaxLimitReachedAlertNotificationVisible ||
    isFiltersNotAppliedNotificationVisible ||
    isMinimumFiltersAppliedNotificationVisible

  const tooltipContent = () => {
    if (hasZeroResults) {
      return i18n.t('SEARCH.TOOLTIP_ZERO_RESULTS')
    }

    if (isSearchModal) {
      return null
    }

    return (
      <p className={`${BASE_CLASS}-tooltipContent`}>
        {i18n.t('SEARCH.TOOLTIP_SAVE')}
        <br />
        {i18n.t('SEARCH.TOOLTIP_ALERT')}
      </p>
    )
  }

  const APP_NAME = config.get('APP_NAME')
  const buttonText =
    customText ||
    (type === buttonTypes.contactSeller ? i18n.t(`COMMON.CREATE_ALERT`) : i18n.t(`CREATE_ALERT_CTA_${APP_NAME}`))

  return (
    <div className={BASE_CLASS}>
      <SearchSaveTooltip
        delay={delay}
        content={tooltipContent()}
        hasZeroResults={hasZeroResults}
        showedZeroResultsTooltip={showedZeroResultsTooltip}
        setIsViewedZeroResultsTooltip={setIsViewedZeroResultsTooltip}
        showTooltip={showTooltip}
      >
        <ButtonCreateAlert
          isFullWidth={isFullWidth}
          size={size}
          type={type}
          onClick={handleCreateAlert}
          isSearchModal={isSearchModal}
          hasZeroResults={hasZeroResults}
          ariaLabel={hideText ? buttonText : ''}
          disabled={disabled}
          icon={icon}
        >
          {!isSearchModal && !hasZeroResults && !hideText && buttonText}
        </ButtonCreateAlert>
      </SearchSaveTooltip>
      {hasToRenderNotification && (
        <>
          {isSuccessNotificationVisible && (
            <SearchSaveSuccessNotification
              // added while we don't have available save maxEntry, maxTerms, reservation and online financing in alerts
              alert={alert}
              show={isSuccessNotificationVisible}
              onClose={hideSuccessNotification}
              portalContainer={portalContainer}
              className={notificationClassName}
            />
          )}
          {isErrorNotificationVisible && (
            <SearchSaveErrorNotification
              show={isErrorNotificationVisible}
              onClose={hideErrorNotification}
              portalContainer={portalContainer}
              className={notificationClassName}
            />
          )}
          {isDuplicatedAlertErrorNotificationVisible && (
            <SearchSaveDuplicatedAlertErrorNotification
              notAvailableFilters={notAvailableFilters}
              onClose={hideDuplicatedAlertErrorNotification}
              show={isDuplicatedAlertErrorNotificationVisible}
              portalContainer={portalContainer}
              className={notificationClassName}
            />
          )}
          {isMaxLimitReachedAlertNotificationVisible && (
            <SearchSaveMaxLimitReachedNotification
              onClose={hideMaxLimitReachedAlertNotification}
              show={isMaxLimitReachedAlertNotificationVisible}
              portalContainer={portalContainer}
              className={notificationClassName}
            />
          )}
          {isFiltersNotAppliedNotificationVisible && (
            <NotificationNoFiltersApplied
              show={isFiltersNotAppliedNotificationVisible}
              onClose={hideFiltersNotAppliedNotification}
              portalContainer={portalContainer}
              className={notificationClassName}
            />
          )}
          {isMinimumFiltersAppliedNotificationVisible && (
            <SearchSaveErrorNotification
              show={isMinimumFiltersAppliedNotificationVisible}
              onClose={hideMinimumFiltersAppliedNotification}
              isMinimumFiltersAppliedError={isMinimumFiltersAppliedNotificationVisible}
              portalContainer={portalContainer}
              className={notificationClassName}
            />
          )}
        </>
      )}
    </div>
  )
}

SearchSave.displayName = 'SearchSave'
SearchSave.propTypes = {
  adId: PropTypes.string,
  createdAlert: PropTypes.object,
  delay: PropTypes.object,
  hasZeroResults: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  isSearchModal: PropTypes.bool,
  search: PropTypes.object.isRequired,
  // Callback to know when login modal is being shown
  setLoginModalVisible: PropTypes.func,
  showTooltip: PropTypes.bool,
  size: PropTypes.oneOf(buttonSizes),
  trackingOrigin: PropTypes.oneOf(Object.values(TRACKING_ORIGINS)),
  authenticationHitInformation2: PropTypes.string,
  type: PropTypes.oneOf(Object.values(buttonTypes)),
  hideText: PropTypes.bool,
  customText: PropTypes.string,
  icon: PropTypes.node,
  disabled: PropTypes.bool,
  onSuccess: PropTypes.func,
  extendedTrackingData: PropTypes.object,
  // Boolean to save alert after user has logged in instead of setting param on url
  createAlertAfterLogin: PropTypes.bool,
  // Optional DOM element to render notifications via React Portal. Useful when SearchSave is inside a container
  // with stacking context issues (e.g., drawers) and notifications need to appear above all other elements.
  // Pass document.body to render notifications at the top level of the page.
  portalContainer: typeof Element !== 'undefined' ? PropTypes.instanceOf(Element) : PropTypes.object,
  notificationClassName: PropTypes.string
}

SearchSave.ErrorNotification = SearchSaveErrorNotification
SearchSave.SuccessNotification = SearchSaveSuccessNotification

export {buttonSizes as searchSaveSizes, buttonTypes as searchSaveTypes, TRACKING_ORIGINS as searchSaveTrackingOrigins}

export default SearchSave
